import type { KeyboardEventHandler, MutableRefObject, Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLayer } from 'react-laag';
import type { ISendEventResponse } from 'matrix-js-sdk';
import type { ReactionClickHandler } from '../chatTypes';
import {
  DEFAULT_PLACEMENT_CONFIG,
  isTouchableDevice,
  mergeRefs
} from '../../../util';

export type UseMessageReactionPickerParams = {
  isSender: boolean;
  reactionCount: number;
  reactionClick: ReactionClickHandler;
  messageRef?: Ref<HTMLDivElement>;
  /** Called after the hook's own navigation. */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  /** ESC on the message itself; return `true` if handled. */
  onEscape?: () => boolean;
  /** Disables reacting, e.g. while sending. */
  disabled?: boolean;
};

/** Reaction picker state, focus and keyboard handling shared by chat messages. */
export function useMessageReactionPicker({
  isSender,
  reactionCount,
  reactionClick,
  messageRef,
  onKeyDown,
  onFocus,
  onEscape,
  disabled = false
}: UseMessageReactionPickerParams) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showReactionPickerTrigger, setShowReactionPickerTrigger] = useState(
    false
  );
  /**
   * True while the emoji picker owns the focus (opened explicitly by the user).
   * When the reaction bar is auto-opened on mobile focus it must not trap the
   * focus, otherwise arrow-key navigation between messages would be blocked.
   */
  const [trapPickerFocus, setTrapPickerFocus] = useState(false);
  const [reactionDrawerOpen, setReactionDrawerOpen] = useState(false);
  const isTouchDevice = isTouchableDevice();

  /** The focusable message container, used to restore focus from the tools. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mergedMessageRef = useMemo(
    () => mergeRefs<HTMLDivElement>([rootRef, messageRef]),
    [messageRef]
  );
  /** The "add reaction" (emoji) button - desktop only. */
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  /** The already existing reaction buttons below the message. */
  const reactionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // drop stale refs when reactions are removed
  reactionRefs.current.length = reactionCount;
  /** Set while the focus is restored programmatically after picking an emoji. */
  const skipMobileAutoOpenRef = useRef(false);

  /**
   * Horizontally navigable tools of a focused message:
   * desktop -> [emoji button, ...reactions], mobile -> [...reactions].
   */
  const getTools = useCallback((): HTMLElement[] => {
    const tools: HTMLElement[] = [];
    if (!isTouchDevice && emojiButtonRef.current) {
      tools.push(emojiButtonRef.current);
    }
    reactionRefs.current.forEach(reaction => {
      if (reaction) {
        tools.push(reaction);
      }
    });
    return tools;
  }, [isTouchDevice]);

  const openReactionPicker = (trapFocus: boolean) => {
    if (disabled) {
      return;
    }
    setTrapPickerFocus(trapFocus);
    setShowReactionPicker(true);
  };

  const closeReactionPicker = useCallback(() => {
    setShowReactionPicker(false);
    setTrapPickerFocus(false);
  }, []);

  // The user may be removed from the room while the picker is open.
  useEffect(() => {
    if (disabled) {
      closeReactionPicker();
    }
  }, [disabled, closeReactionPicker]);

  /**
   * Moves the focus back onto the message container. Deferred to the next
   * frame so it runs after the picker layer (and its focus trap) has been
   * unmounted - otherwise the focus would end up on the document body.
   *
   * @param skipMobileAutoOpen pass `true` when the user explicitly dismissed
   * the picker (Escape inside the picker, emoji selected). Without it the
   * mobile reaction bar would immediately pop up again, because on mobile it
   * follows the focus of the message.
   */
  const focusMessage = useCallback((skipMobileAutoOpen = false) => {
    skipMobileAutoOpenRef.current = skipMobileAutoOpen;
    requestAnimationFrame(() => {
      rootRef.current?.focus();
      skipMobileAutoOpenRef.current = false;
    });
  }, []);

  /**
   * Picking an emoji closes the picker and removes the trigger button, so the
   * currently focused element disappears. Keep the focus on the message the
   * reaction belongs to instead of losing it to the document body.
   */
  const handlePickerReactionClick = useCallback(
    (
      reactionEventId: string,
      reactionKey: string,
      reactionRoomId?: string
    ): Promise<ISendEventResponse | undefined> => {
      closeReactionPicker();
      focusMessage(true);
      return reactionClick(reactionEventId, reactionKey, reactionRoomId);
    },
    [closeReactionPicker, focusMessage, reactionClick]
  );

  const toggleReactionPicker = (show: boolean) => {
    if (show) {
      openReactionPicker(true);
    } else {
      const hadFocusInside = trapPickerFocus;
      closeReactionPicker();
      if (hadFocusInside) {
        // closing via Escape or the trigger button - the focused element inside
        // the picker is removed, so put the focus back on the message. The
        // close was explicit, so the mobile bar stays closed for now.
        focusMessage(true);
      }
    }
  };

  const isInsidePicker = (element: EventTarget | null) =>
    Boolean((element as HTMLElement | null)?.closest?.('.EmojiPickerReact'));

  const handleFocus = (focusItem: React.FocusEvent<HTMLDivElement>) => {
    onFocus?.(focusItem);
    // Focus events of the picker bubble through the portal - they must not
    // change the open state of the picker itself.
    if (isInsidePicker(focusItem.target)) {
      return;
    }
    if (isTouchDevice) {
      // On mobile there is no emoji button - the reaction bar follows the
      // focus: it is shown while the message itself is focused and hidden as
      // soon as the arrow keys move on to one of the reaction buttons.
      const messageItselfFocused = focusItem.target === focusItem.currentTarget;
      if (messageItselfFocused) {
        if (!showReactionPicker && !skipMobileAutoOpenRef.current) {
          openReactionPicker(false);
        }
      } else if (showReactionPicker && !trapPickerFocus) {
        closeReactionPicker();
      }
      return;
    }
    if (!showReactionPickerTrigger) {
      setShowReactionPickerTrigger(true);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (
      (nextTarget && event.currentTarget.contains(nextTarget)) ||
      isInsidePicker(nextTarget)
    ) {
      // focus stays within the message (or moved into the picker layer)
      return;
    }
    if (!trapPickerFocus) {
      closeReactionPicker();
    }
    if (!isTouchDevice) {
      setShowReactionPickerTrigger(false);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    // Key events of the picker bubble up through the portal. While the picker
    // traps the focus it handles them itself (its own navigation, ESC closes
    // it via the focus trap). The auto-opened mobile bar does NOT trap the
    // focus, so nothing would handle ESC there - it has to be handled here.
    const fromPicker = isInsidePicker(event.target);
    if (fromPicker && trapPickerFocus) {
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      if (trapPickerFocus || fromPicker) {
        // the emoji picker handles its own navigation
        return;
      }
      const tools = getTools();
      if (tools.length === 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      // The message itself is part of the ring:
      // message -> tool 1 -> ... -> tool n -> message -> ...
      // Landing on the message again shows the picker (mobile) resp. the
      // emoji button (desktop), because both follow the message focus.
      const ringLength = tools.length + 1;
      const forward = event.key === 'ArrowRight';
      // index 0 is the message, the tools start at 1
      const current = tools.indexOf(document.activeElement as HTMLElement) + 1;
      const next = (current + (forward ? 1 : -1) + ringLength) % ringLength;
      if (next === 0) {
        focusMessage();
      } else {
        tools[next - 1]?.focus();
      }
      return;
    }

    if (event.key === 'Escape') {
      if (fromPicker) {
        // Untrapped picker (the mobile reaction bar): there is no focus trap
        // that could close it, so do it here and take the focus back.
        event.stopPropagation();
        closeReactionPicker();
        focusMessage(true);
        return;
      }
      const focusIsOnTool = event.target !== event.currentTarget;
      if (focusIsOnTool) {
        // Escape leaves the tools and returns to the message. On mobile that
        // re-opens the reaction bar, because it follows the focus.
        event.stopPropagation();
        closeReactionPicker();
        focusMessage();
        return;
      }
      // The message itself is focused. On mobile the reaction bar follows that
      // focus, so it cannot be closed while the message stays focused - ESC
      // therefore leaves the message list, which closes the bar as well.
      closeReactionPicker();
      if (onEscape?.()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (showReactionPicker) {
        // nowhere to move the focus to - at least close the picker
        event.stopPropagation();
        return;
      }
    }

    onKeyDown?.(event);
  };

  const innerMessageRef = useRef<HTMLDivElement>(null);
  const getContainer = useCallback(() => innerMessageRef.current, []);
  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: showReactionPicker,
    onOutsideClick: () => {
      closeReactionPicker();
    },
    container: getContainer?.() ?? undefined,
    ...DEFAULT_PLACEMENT_CONFIG,
    triggerOffset: 0,
    containerOffset: 0,
    placement: isSender ? 'top-end' : 'top-start'
  });

  return {
    isTouchDevice,
    mergedMessageRef,
    emojiButtonRef,
    reactionRefs: reactionRefs as MutableRefObject<
      (HTMLButtonElement | null)[]
    >,
    showReactionPicker,
    showReactionPickerTrigger,
    setShowReactionPickerTrigger,
    trapPickerFocus,
    toggleReactionPicker,
    openReactionPicker,
    closeReactionPicker,
    handlePickerReactionClick,
    handleFocus,
    handleBlur,
    handleKeyDown,
    layerProps,
    renderLayer,
    triggerRef: triggerProps.ref,
    reactionDrawerOpen,
    setReactionDrawerOpen
  };
}
